export const CHALLENGE_PROJECT_DRAFT_PATH = "Chapters/challenge-draft.md";
export const CHALLENGE_PROJECT_BRIEF_PATH = "Notes/challenge-brief.md";
export const CHALLENGE_PROJECT_SUBMISSION_PATH = "Chapters/challenge-submission.md";
export const CHALLENGE_PROJECT_LEGACY_SUBMISSION_PATH = "challenge-submission.md";

export const isChallengeSubmissionPath = (path: string) =>
  path === CHALLENGE_PROJECT_SUBMISSION_PATH ||
  path === CHALLENGE_PROJECT_LEGACY_SUBMISSION_PATH;

export type ChallengeProjectSeedInput = {
  title: string;
  description: string;
  genre: string;
  constraintLabel: string;
  reviewQueueLabel: string;
  rewardLabels?: string[];
  existingContent?: string;
};

const buildOutlineContent = (input: ChallengeProjectSeedInput) => `# Outline: ${input.title}

## Prompt
${input.description}

## Project Frame
- Genre: ${input.genre}
- Constraint: ${input.constraintLabel}
- Review queue: ${input.reviewQueueLabel}

## Expansion beats
1. Define who is at the center of this prompt and what they stand to lose.
2. Decide what pressure, discovery, or reversal makes the scene impossible to ignore.
3. Map the immediate consequence that could grow this into a chapter, arc, or full manuscript.
4. Note the unresolved question that should keep the reader moving forward.
`;

const buildBriefContent = (input: ChallengeProjectSeedInput) => {
  const rewards =
    input.rewardLabels && input.rewardLabels.length > 0
      ? input.rewardLabels.join(", ")
      : "None listed";

  return `# Challenge Brief: ${input.title}

## Original Prompt
${input.description}

## Submission Rules
- Constraint: ${input.constraintLabel}
- Review queue: ${input.reviewQueueLabel}
- Rewards: ${rewards}

## How To Use This Workspace
1. Draft scenes, alternates, and exploratory material in \`${CHALLENGE_PROJECT_DRAFT_PATH}\`.
2. Place the final text you want judged or published in \`${CHALLENGE_PROJECT_SUBMISSION_PATH}\`.
3. Stretch the prompt into scenes, chapters, or a full outline in \`outline.md\`.
4. Add canon details to Characters, World Building, and Notes as the story grows.
`;
};

const buildDraftContent = (input: ChallengeProjectSeedInput) => {
  const existing = input.existingContent?.trim();
  const header = `# ${input.title}

> Seeded from a Share-Pen challenge prompt.
> Constraint: ${input.constraintLabel}
> Queue: ${input.reviewQueueLabel}

`;

  if (existing) {
    return `${header}${existing}
`;
  }

  return `${header}## Draft

Start from the tension, image, or question that made you open this challenge.
`;
};

const buildSubmissionContent = (input: ChallengeProjectSeedInput) => {
  const existing = input.existingContent?.trim();

  if (existing) {
    return `${existing}\n`;
  }

  return "";
};

export const buildChallengeProjectSeed = (input: ChallengeProjectSeedInput) => ({
  outlineContent: buildOutlineContent(input),
  briefContent: buildBriefContent(input),
  draftContent: buildDraftContent(input),
  submissionContent: buildSubmissionContent(input),
});
